/* ==========================================================================
   Oddit interactions — runtime for entrance/reveal animations and stagger.
   Adds .is-visible to .oddit-entrance and .oddit-reveal elements when they
   enter the viewport. Applies sequential transition delays to .oddit-reveal
   children of any [data-oddit-stagger] container.
   ========================================================================== */

(function () {
  'use strict';

  var STAGGER_STEP_MS = 80;
  var REVEAL_SELECTOR = '.oddit-entrance:not(.is-visible), .oddit-reveal:not(.is-visible)';

  var prefersReducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function markAllVisible(root) {
    var scope = root || document;
    scope.querySelectorAll('.oddit-entrance, .oddit-reveal').forEach(function (el) {
      el.classList.add('is-visible');
    });
  }

  function applyStagger(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-oddit-stagger]').forEach(function (group) {
      var children = group.querySelectorAll('.oddit-reveal, .oddit-entrance');
      children.forEach(function (child, index) {
        if (!child.style.transitionDelay) {
          child.style.transitionDelay = (index * STAGGER_STEP_MS) + 'ms';
        }
      });
    });
  }

  var observer = null;

  function getObserver() {
    if (observer || !('IntersectionObserver' in window)) return observer;
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -2% 0px', threshold: 0.04 });
    return observer;
  }

  function observeAll(root) {
    var scope = root || document;
    var io = getObserver();
    if (!io) {
      markAllVisible(scope);
      return;
    }
    scope.querySelectorAll(REVEAL_SELECTOR).forEach(function (el) {
      io.observe(el);
    });
  }

  /* ---------- Parallax ---------- */
  var parallaxItems = [];
  var parallaxTicking = false;

  function collectParallax(root) {
    var scope = root || document;
    scope.querySelectorAll('.oddit-parallax').forEach(function (el) {
      if (parallaxItems.some(function (item) { return item.el === el; })) return;
      var speed = parseFloat(el.getAttribute('data-oddit-parallax-speed'));
      if (isNaN(speed)) speed = 0.1;
      parallaxItems.push({ el: el, speed: speed });
    });
  }

  function updateParallax() {
    parallaxTicking = false;
    var vpH = window.innerHeight;
    for (var i = 0; i < parallaxItems.length; i++) {
      var item = parallaxItems[i];
      var rect = item.el.getBoundingClientRect();
      if (rect.bottom < -vpH || rect.top > vpH * 2) continue;
      var offset = -rect.top * item.speed;
      item.el.style.transform = 'translate3d(0, ' + offset.toFixed(2) + 'px, 0)';
    }
  }

  function onParallaxScroll() {
    if (parallaxTicking) return;
    parallaxTicking = true;
    window.requestAnimationFrame(updateParallax);
  }

  function initParallax(root) {
    collectParallax(root);
    if (parallaxItems.length === 0) return;
    updateParallax();
    window.addEventListener('scroll', onParallaxScroll, { passive: true });
    window.addEventListener('resize', onParallaxScroll, { passive: true });
  }

  /* ---------- Word rotator ---------- */
  var rotatorTimers = [];
  var WORD_OUT_MS = 260;

  function initWordRotators(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-oddit-word-rotator]').forEach(function (container) {
      var em = container.querySelector('em');
      if (!em || em.dataset.odditRotatorReady === 'true') return;

      var raw = container.getAttribute('data-oddit-word-rotator-words') || '';
      var extras = raw.split('|').map(function (w) { return w.trim(); }).filter(Boolean);
      if (extras.length === 0) return;

      var interval = parseInt(container.getAttribute('data-oddit-word-rotator-interval'), 10);
      if (!interval || interval < 1000) interval = 3000;

      var initialText = em.textContent.trim();
      var sequence = [initialText].concat(extras);

      var word = document.createElement('span');
      word.className = 'oddit-word-rotator__word';
      word.textContent = initialText;

      var wrap = document.createElement('span');
      wrap.className = 'oddit-word-rotator';
      wrap.setAttribute('aria-live', 'polite');
      wrap.appendChild(word);

      em.textContent = '';
      em.appendChild(wrap);
      em.dataset.odditRotatorReady = 'true';

      // Offscreen measurer to size the wrapper to fit the longest word.
      // Locking width to the longest word keeps preceding text ("Feel more") stable.
      var measurer = document.createElement('span');
      measurer.style.visibility = 'hidden';
      measurer.style.position = 'absolute';
      measurer.style.whiteSpace = 'nowrap';
      measurer.style.pointerEvents = 'none';
      measurer.style.top = '0';
      measurer.style.left = '0';
      wrap.appendChild(measurer);

      var maxWidth = 0;
      for (var s = 0; s < sequence.length; s++) {
        measurer.textContent = sequence[s];
        if (measurer.offsetWidth > maxWidth) maxWidth = measurer.offsetWidth;
      }
      wrap.style.width = maxWidth + 'px';
      wrap.style.textAlign = 'left';

      // Typewriter-style rotation: delete chars, then type next word.
      var TYPE_MS = 60;
      var DELETE_MS = 40;
      var idx = 0;

      function deleteWord(cb) {
        var current = word.textContent;
        if (current.length === 0) { cb(); return; }
        var t = setInterval(function () {
          var t2 = word.textContent;
          word.textContent = t2.slice(0, -1);
          if (word.textContent.length === 0) {
            clearInterval(t);
            cb();
          }
        }, DELETE_MS);
        rotatorTimers.push(t);
      }

      function typeWord(text, cb) {
        var i = 0;
        var t = setInterval(function () {
          word.textContent += text.charAt(i);
          i++;
          if (i >= text.length) {
            clearInterval(t);
            cb && cb();
          }
        }, TYPE_MS);
        rotatorTimers.push(t);
      }

      var timer = setInterval(function () {
        var next = (idx + 1) % sequence.length;
        deleteWord(function () {
          typeWord(sequence[next]);
          idx = next;
        });
      }, interval);

      rotatorTimers.push(timer);
    });
  }

  function init(root) {
    if (prefersReducedMotion) {
      markAllVisible(root);
      initWordRotators(root);
      return;
    }
    applyStagger(root);
    observeAll(root);
    initParallax(root);
    initWordRotators(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Re-init on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
